/**
 * Az edzés-ábra adatmodellje. Egy ábra egy pályasablonból és a rá rakott
 * elemekből áll; a koordináták a 1050 x 680 egységnyi rajzlapon értendők
 * (egy teljes pálya 105 x 68 méter, tehát 1 egység = 10 cm).
 */

export const CANVAS_WIDTH = 1050;
export const CANVAS_HEIGHT = 680;

export type PitchType = 'full' | 'half' | 'blank';

/** A játékosok és a zónák színei - csapatonként egy. */
export type TeamColor = 'red' | 'blue' | 'yellow' | 'white' | 'black';

export const TEAM_COLORS: Record<TeamColor, { fill: string; text: string; label: string }> = {
  red: { fill: '#dc2626', text: '#ffffff', label: 'Piros' },
  blue: { fill: '#2563eb', text: '#ffffff', label: 'Kék' },
  yellow: { fill: '#facc15', text: '#1f2937', label: 'Sárga' },
  white: { fill: '#f8fafc', text: '#1f2937', label: 'Fehér' },
  black: { fill: '#111827', text: '#ffffff', label: 'Fekete' },
};

/** A vonalak és szövegek színei. */
export const INK_COLORS = ['#ffffff', '#111827', '#dc2626', '#2563eb', '#facc15'] as const;

/** Futás: szaggatott nyíl; passz: folytonos nyíl; labdavezetés: hullámos nyíl; vonal: nyíl nélkül. */
export type LineStyle = 'pass' | 'shot' | 'run' | 'dribble' | 'line';

export type EquipmentKind = 'ball' | 'cone' | 'pole' | 'minigoal' | 'goal' | 'disc';

interface BaseElement {
  id: string;
}

export interface PlayerElement extends BaseElement {
  type: 'player';
  x: number;
  y: number;
  color: TeamColor;
  label: string;
}

export interface EquipmentElement extends BaseElement {
  type: 'equipment';
  kind: EquipmentKind;
  x: number;
  y: number;
  /** Elforgatás fokban (a kapuknál számít). */
  rotation: number;
  color: string;
}

export interface LineElement extends BaseElement {
  type: 'line';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** A görbe kontrollpontja; ha nincs, a vonal egyenes. */
  cx?: number;
  cy?: number;
  style: LineStyle;
  color: string;
}

export interface ZoneElement extends BaseElement {
  type: 'zone';
  shape: 'rect' | 'ellipse';
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
}

export interface TextElement extends BaseElement {
  type: 'text';
  x: number;
  y: number;
  text: string;
  color: string;
  size: number;
}

export type DiagramElement = PlayerElement | EquipmentElement | LineElement | ZoneElement | TextElement;

export interface Diagram {
  version: 1;
  pitch: PitchType;
  elements: DiagramElement[];
}

export const emptyDiagram = (): Diagram => ({ version: 1, pitch: 'full', elements: [] });

/** A tárolt JSON beolvasása; hibás vagy üres adatnál null. */
export const parseDiagram = (json?: string | null): Diagram | null => {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (parsed && Array.isArray(parsed.elements)) {
      return {
        version: 1,
        pitch: ['full', 'half', 'blank'].includes(parsed.pitch) ? parsed.pitch : 'full',
        elements: parsed.elements,
      };
    }
  } catch {
    // hibás JSON: úgy kezeljük, mintha nem lenne rajz
  }
  return null;
};

let idCounter = 0;
export const newId = () => {
  idCounter += 1;
  return `${Date.now().toString(36)}-${idCounter}`;
};
